import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Stack,
  Collapse,
  Button,
  Skeleton,
} from "@mui/material";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CompetitionPodium from "./CompetitionPodium";
import CompetitionVideoModal from "./CompetitionVideoModal";
import { contestantDisplayName } from "../../../utils/contestantDisplayName";
import {
  LIST_MEDIA_BG,
  listRowBackground,
  listRowBorderColor,
} from "../../../utils/siteListTheme";

function useCountUp(target, duration = 1100, delay = 0) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    let rafId;
    let startTime = null;
    const tid = setTimeout(() => {
      const step = (ts) => {
        if (!startTime) startTime = ts;
        const p = Math.min((ts - startTime) / duration, 1);
        setValue(Math.floor(target * (1 - Math.pow(1 - p, 3))));
        if (p < 1) rafId = requestAnimationFrame(step);
        else setValue(target);
      };
      rafId = requestAnimationFrame(step);
    }, delay);
    return () => {
      clearTimeout(tid);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [target, duration, delay]);
  return value;
}

function displayName(row) {
  return contestantDisplayName(row);
}

/** Lazy thumbnail — only loads when scrolled into view */
function LazyThumb({ src, alt = "", onClick, sx }) {
  const ref = useRef(null);
  const [ready, setReady] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setReady(true);
          obs.disconnect();
        }
      },
      { rootMargin: "120px", threshold: 0.01 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <Box
      ref={ref}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => {
        if (onClick && (e.key === "Enter" || e.key === " ")) onClick(e);
      }}
      sx={{
        position: "relative",
        overflow: "hidden",
        bgcolor: LIST_MEDIA_BG,
        cursor: onClick ? "pointer" : "default",
        ...sx,
      }}
    >
      {!loaded ? (
        <Skeleton
          variant="rectangular"
          animation="wave"
          sx={{
            position: "absolute",
            inset: 0,
            bgcolor: "rgba(255,255,255,0.06)",
          }}
        />
      ) : null}
      {ready && src ? (
        <Box
          component="img"
          src={src}
          alt={alt}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
            opacity: loaded ? 1 : 0,
            transition: "opacity 0.3s ease",
          }}
        />
      ) : null}
      {onClick ? (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.4) 100%)",
            color: "#fff",
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              bgcolor: "rgba(239,54,78,0.9)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PlayCircleOutlineIcon sx={{ fontSize: 22 }} />
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}

function PositionRow({
  row,
  position,
  index,
  maxVotes = 1,
  votingEnabled,
  onOpenVideo,
}) {
  const rowRef = useRef(null);
  const [active, setActive] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const stagger = Math.min(index * 55, 550);
  const isTop3 = position <= 3;
  const isFirst = position === 1;
  const voteCount = row.voteCount ?? row.vote_count ?? 0;
  const animatedVotes = useCountUp(
    voteCount,
    1100,
    active ? stagger + 180 : 9999999
  );
  const avatar =
    row.contestant?.profile_image || row.contestant?.user?.profile_img;
  const name = displayName(row);
  const videos = Array.isArray(row.videos) ? row.videos : [];
  const videoCount = row.videoCount || videos.length || (row.video_url ? 1 : 0);
  const statsLine =
    row.statsLine ||
    `${videoCount} upload${videoCount === 1 ? "" : "s"} · total votes`;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setActive(true), stagger);
          observer.disconnect();
        }
      },
      { threshold: 0.08 }
    );
    if (rowRef.current) observer.observe(rowRef.current);
    return () => observer.disconnect();
  }, [stagger]);

  const borderColor = listRowBorderColor({ isFirst, isTop3, hovered });
  const bg = listRowBackground({ isFirst, isTop3, hovered });

  return (
    <Box
      ref={rowRef}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        opacity: active ? 1 : 0,
        transform: active
          ? hovered
            ? "translateY(-2px)"
            : "translateY(0)"
          : "translateY(14px)",
        transition:
          "opacity 0.4s ease, transform 0.28s ease, box-shadow 0.28s ease",
        position: "relative",
        borderRadius: 2.5,
        overflow: "hidden",
        mb: 1.25,
        background: bg,
        border: `1px solid ${borderColor}`,
        boxShadow: isFirst
          ? "0 10px 28px rgba(240,199,94,0.12)"
          : isTop3
            ? "0 8px 22px rgba(0,0,0,0.25)"
            : hovered
              ? "0 10px 24px rgba(239,54,78,0.12)"
              : "none",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap={{ xs: 1.2, sm: 2 }}
        sx={{ p: { xs: 1.2, sm: 1.6 } }}
      >
        <Box
          sx={{
            width: 40,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.3,
          }}
        >
          {isFirst ? (
            <Typography
              sx={{
                fontSize: 22,
                lineHeight: 1,
                color: "#F0C75E",
                filter: "drop-shadow(0 0 8px rgba(240,199,94,0.6))",
              }}
              aria-label="1st position"
            >
              ♛
            </Typography>
          ) : null}
          <Typography
            sx={{
              fontWeight: 800,
              fontFamily: "Montserrat, sans-serif",
              fontSize: isTop3 ? 17 : 14,
              color: isFirst
                ? "#F0C75E"
                : isTop3
                  ? "#E8C4A8"
                  : "rgba(255,255,255,0.4)",
            }}
          >
            {position}
          </Typography>
        </Box>

        <Avatar
          src={avatar || ""}
          sx={{
            width: { xs: 44, sm: 52 },
            height: { xs: 44, sm: 52 },
            flexShrink: 0,
            border: isFirst
              ? "2px solid #F0C75E"
              : isTop3
                ? "2px solid rgba(232,196,168,0.7)"
                : "2px solid rgba(255,255,255,0.12)",
          }}
        >
          {name[0]}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Typography
              noWrap
              sx={{
                color: "#fff",
                fontWeight: 700,
                fontFamily: "Montserrat, sans-serif",
                fontSize: { xs: 14, sm: 15 },
              }}
            >
              {name}
            </Typography>
            {isTop3 ? (
              <Box
                component="span"
                sx={{
                  px: 1,
                  py: 0.2,
                  borderRadius: 1,
                  fontSize: 11,
                  fontWeight: 700,
                  bgcolor: isFirst
                    ? "rgba(240,199,94,0.2)"
                    : "rgba(232,196,168,0.15)",
                  color: isFirst ? "#F0C75E" : "#E8C4A8",
                  border: `1px solid ${
                    isFirst
                      ? "rgba(240,199,94,0.4)"
                      : "rgba(232,196,168,0.35)"
                  }`,
                }}
              >
                Top {position}
              </Box>
            ) : null}
          </Stack>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.5)",
              fontSize: 12,
              mt: 0.4,
              fontWeight: 600,
            }}
          >
            {statsLine}
          </Typography>
          <Box
            sx={{
              mt: 0.8,
              height: 3,
              borderRadius: 2,
              bgcolor: "rgba(255,255,255,0.08)",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                height: "100%",
                width: active
                  ? `${Math.min((voteCount / maxVotes) * 100, 100)}%`
                  : 0,
                background: isFirst
                  ? "linear-gradient(90deg, #FFE08A, #F0C75E)"
                  : isTop3
                    ? "linear-gradient(90deg, #E8C4A8, #C9956C)"
                    : "linear-gradient(90deg, #ef364e, #c43a4e)",
                transition: "width 1.1s cubic-bezier(0.25,0.46,0.45,0.94)",
              }}
            />
          </Box>
        </Box>

        <Box sx={{ textAlign: "right", flexShrink: 0 }}>
          <Typography
            sx={{
              color: isFirst ? "#F0C75E" : isTop3 ? "#E8C4A8" : "#fff",
              fontWeight: 800,
              fontFamily: "Montserrat, sans-serif",
              fontSize: 17,
              lineHeight: 1,
            }}
          >
            {animatedVotes}
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.4)",
              fontSize: 11,
              fontWeight: 600,
              mt: 0.4,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            votes
          </Typography>
        </Box>
      </Stack>

      {videoCount > 0 ? (
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          <Button
            size="small"
            endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setExpanded((v) => !v)}
            sx={{
              textTransform: "none",
              color: "rgba(255,255,255,0.75)",
              fontWeight: 600,
              fontSize: 13,
              px: 1,
              "&:hover": { bgcolor: "rgba(255,255,255,0.05)" },
            }}
          >
            {expanded
              ? "Hide uploads"
              : `Show ${videoCount} upload${videoCount === 1 ? "" : "s"}`}
          </Button>

          <Collapse in={expanded} unmountOnExit>
            <Stack
              direction="row"
              gap={1.25}
              flexWrap="wrap"
              sx={{ mt: 1.25 }}
            >
              {(videos.length
                ? videos
                : row.video_url
                  ? [
                      {
                        id: row.id,
                        video_url: row.video_url,
                        thumbnail: row.thumbnail,
                        voteCount: voteCount,
                        category: row.category,
                        event: row.event,
                      },
                    ]
                  : []
              ).map((v) => (
                <Box
                  key={v.id || v.video_url}
                  sx={{
                    width: { xs: "calc(50% - 6px)", sm: 168 },
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid rgba(65,68,102,0.85)",
                    bgcolor: "rgba(36,39,67,0.55)",
                    transition: "border-color 0.2s ease, transform 0.2s ease",
                    "&:hover": {
                      borderColor: "rgba(239,54,78,0.45)",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <LazyThumb
                    src={v.thumbnail || ""}
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenVideo?.(v, row);
                    }}
                    sx={{ width: "100%", height: 96 }}
                  />
                  <Box sx={{ p: 1 }}>
                    <Typography
                      noWrap
                      sx={{
                        color: "#fff",
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {v.event?.name || "Event"}
                    </Typography>
                    <Typography
                      noWrap
                      sx={{
                        color: "rgba(255,255,255,0.45)",
                        fontSize: 11,
                        mt: 0.2,
                      }}
                    >
                      {v.category || "—"} · {v.voteCount ?? v.vote_count ?? 0}{" "}
                      votes
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Collapse>
        </Box>
      ) : null}
    </Box>
  );
}

/**
 * Contestant positions + expandable submitted videos (lazy-loaded thumbs).
 */
export default function CompetitionLeaderboard({
  rows = [],
  videos,
  votingEnabled = false,
  categoryTitle = "",
}) {
  const [activeVideo, setActiveVideo] = useState(null);
  const list = useMemo(
    () => (rows?.length ? rows : videos || []),
    [rows, videos]
  );
  const sorted = useMemo(() => {
    return [...list].sort((a, b) => {
      const av = a.voteCount ?? a.vote_count ?? 0;
      const bv = b.voteCount ?? b.vote_count ?? 0;
      return bv - av;
    });
  }, [list]);

  const maxVotes = useMemo(() => {
    const top = sorted[0];
    const n = top?.voteCount ?? top?.vote_count ?? 0;
    return Math.max(n, 1);
  }, [sorted]);

  const openVideo = (video, contestantRow) => {
    if (!video?.video_url) return;
    setActiveVideo({
      ...video,
      contestant: contestantRow?.contestant || video.contestant,
      voteCount: video.voteCount ?? video.vote_count ?? 0,
    });
  };

  return (
    <Box>
      <CompetitionPodium rows={sorted} categoryTitle={categoryTitle} />
      <Typography
        sx={{
          color: "#fff",
          fontWeight: 800,
          fontFamily: "Montserrat, sans-serif",
          fontSize: 18,
          mb: 1.5,
        }}
      >
        Position
      </Typography>
      {sorted.length === 0 ? (
        <Box
          sx={{
            border: "1px dashed rgba(255,255,255,0.14)",
            borderRadius: 3,
            p: 5,
            textAlign: "center",
            color: "rgba(255,255,255,0.5)",
          }}
        >
          No contestants ranked yet.
        </Box>
      ) : (
        sorted.map((r, i) => (
          <PositionRow
            key={r.id || r.contestant_id || i}
            row={r}
            position={r.rank || i + 1}
            index={i}
            maxVotes={maxVotes}
            votingEnabled={votingEnabled}
            onOpenVideo={openVideo}
          />
        ))
      )}
      <CompetitionVideoModal
        open={Boolean(activeVideo)}
        onClose={() => setActiveVideo(null)}
        video={activeVideo}
        votingEnabled={votingEnabled}
      />
    </Box>
  );
}
